import { Button, Stack, Box, Checkbox } from "@mui/material";
import React, { useEffect } from "react";
import { tease } from "../components/CheckButton";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import {
  ActionType,
  Color,
  ContactType,
  ErrorCode,
  FontStyle,
  navPaddingTop,
  Slug,
  SurveyButtonStyle,
} from "../constants";
import FavoriteIcon from "@mui/icons-material/Favorite";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { StyledTextField } from "../components/AutocompleteUtils";
import * as apiService from "../service/ApiService";
import { useDispatch, useSelector } from "react-redux";
import googleIcon from "../img/googleIcon.png";
import { useNavigate } from "react-router-dom";

const styles = {
  outer: {
    paddingTop: navPaddingTop,
  },
  container: {
    // marginTop: '67px',
    paddingTop: "3rem",
    paddingBottom: "110px",
    px: 4,
    backgroundColor: "#fff",
    height: "100%",
    display: "flex",
    alignItems: "stretch",
    minHeight: "calc(100vh - 67px)",
  },
  inner: {
    width: "100%",
    maxWidth: "24.38rem",
    mx: "auto",
    textAlign: "center",
  },
  heading: {
    ...FontStyle.title,
    fontSize: "1.73rem",
  },
  panel: {
    width: "100%",
    // backgroundColor: '#fff',
    // borderRadius: '0.75rem',
    // padding: '1.2rem',
    "& .MuiFormControl-root": {
      border: "none",
    },
  },
  label: {
    ...FontStyle.headingBody,
    fontSize: "1.2rem",
  },
  search: {
    width: "100%",
    maxWidth: "500px",
    backgroundColor: "rgb(255, 255, 255)",
    borderRadius: "4px",
    border: "1px solid rgb(204, 204, 204)",
  },
  button: {
    ...FontStyle.headingBody,
    width: "100%",
    fontSize: "1rem",
    textTransform: "none",
    color: "black",
    borderRadius: "6.25rem",
    padding: "0.4rem 2rem",
    backgroundColor: Color.primary,
    border: `1px solid ${Color.primary}`,
    "&:hover": {
      backgroundColor: Color.primaryDark,
    },
    transition: "all 0.3s ease-in-out 0s",
  },
  buttonWhite: {
    backgroundColor: "white",
    border: "none",
    "&:hover": {
      backgroundColor: "rgb(252,252,252)",
    },
  },
  link: {
    ...FontStyle.headingBody,
    width: "100%",
    textAlign: "center",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  forgotPassword: {
    ...FontStyle.body,
    width: "100%",
    textAlign: "left",
    textDecoration: "underline",
    cursor: "pointer",
    "&:hover": {
      color: "green",
    },
    // fontSize: "14px",
  },
  noAccount: {
    ...FontStyle.headingBody,
    textAlign: "center",
  },
  body: {
    ...FontStyle.body,
    fontSize: "1rem",
    textAlign: "left",
    lineHeight: "1.3",
  },
  orText: {
    ...FontStyle.body,
    fontSize: "0.83rem",
    lineHeight: "1.3",
    textAlign: "inherit",
    color: "rgb(160, 160, 160)",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  bar: {
    width: "40%",
    height: "2px",
    backgroundColor: "rgb(160, 160, 160)",
  },
  googleCustomButton: {
    textTransform: "none",
    fontFamily: '"Google Sans",arial,sans-serif',
    fontWeight: "500",
    height: "46px",
    width: "100%",
    border: "1px solid #dadce0",
    color: "#202124",
    "&:hover": {
      border: "1px solid #d2e3fc",
    },
  },
  error: {
    ...FontStyle.body,
    color: "red",
    fontSize: "0.75rem",
    textAlign: "left",
    width: "100%",
  },
};

const LabelInputStack = ({ label, children }) => {
  return (
    <Stack
      direction="column"
      spacing="0.5rem"
      sx={{
        width: "100%",
      }}
      alignItems="flex-start">
      <Box sx={{ ...styles.label, fontSize: "1rem" }}>{label}</Box>
      {children}
    </Stack>
  );
};

// ---swap color scheme
// -add google log in
// -reference sign up page
//   -swap buttons/boxes styling
// -swap bottom bar's "or" to just a solid bar
//   -look at spotify https://accounts.spotify.com/en/login?continue=https%3A%2F%2Fopen.spotify.com%2F
const Login = () => {
  const queryParameters = new URLSearchParams(window.location.search);
  const jobToken = queryParameters.get("job_id");
  const eduId = queryParameters.get("post"); // from university
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [stayLoggedIn, setStayLoggedIn] = React.useState(true);
  const googleCallback = (response) => {
    console.log("got google response", response);
    const { access_token } = response;
    if (access_token) {
      // apiService.register(dispatch, {
      //   password: credential,
      //   contactType: ContactType.google,
      // });
      apiService.login(dispatch, navigate, {
        // contactInfo: email,
        password: access_token,
        contactType: ContactType.google,
        stayLoggedIn,
        jobToken,
        eduId,
      });
    }
  };
  const customGoogleLogin = useGoogleLogin({
    onSuccess: googleCallback,
    onError: (e) => console.log("Login Failed", e),
  });
  const handleLogin = () => {
    dispatch({ type: ActionType.error, payload: { error_code: null } });
    apiService.login(dispatch, navigate, {
      contactInfo: email,
      password,
      contactType: ContactType.email,
      stayLoggedIn,
      jobToken,
      eduId,
    });
  };
  const submitOnEnter = (e) => {
    if (e.code === "Enter") {
      handleLogin();
    }
  };
  const canLogin = email && password;

  return (
    <Box sx={styles.outer}>
      <Box sx={styles.container}>
        <Stack
          sx={styles.inner}
          direction="column"
          alignItems="center"
          spacing="1rem">
          <Box sx={styles.heading}>Log in</Box>
          <Box sx={{ paddingTop: 2, paddingBottom: 2, width: "100%" }}>
            <Button sx={styles.googleCustomButton} onClick={customGoogleLogin}>
              <img
                src={googleIcon}
                style={{ width: "18px", height: "18px", marginRight: "8px" }}
              />
              Sign in with Google
            </Button>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%" }}>
            <Box sx={styles.bar} />
            <Box sx={styles.orText}>OR</Box>
            <Box sx={styles.bar} />
          </Stack>
          <Stack
            sx={styles.panel}
            direction="column"
            alignItems="center"
            spacing="1rem">
            <LabelInputStack label="Email">
              <StyledTextField
                InputProps={{ disableUnderline: true }}
                sx={styles.search}
                variant="standard"
                placeholder=""
                value={email}
                onKeyDown={submitOnEnter}
                onChange={(e) => setEmail(e.target.value)}
              />
            </LabelInputStack>
            <LabelInputStack label="Password">
              <StyledTextField
                InputProps={{ disableUnderline: true }}
                sx={styles.search}
                variant="standard"
                type="password"
                placeholder=""
                value={password}
                onKeyDown={submitOnEnter}
                onChange={(e) => setPassword(e.target.value)}
              />
            </LabelInputStack>
            {auth.error === ErrorCode.invalidCredentials && (
              <Box sx={styles.error}>Invalid Login</Box>
            )}
            <Box
              onClick={() => navigate(Slug.forgotPassword)}
              sx={{
                ...styles.forgotPassword,
              }}>
              Forgot your password?
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            // justifyContent="flex-start"
            alignItems="end"
            sx={{ paddingTop: 1, cursor: "pointer", width: "100%" }}
            onClick={() => setStayLoggedIn(!stayLoggedIn)}>
            <Checkbox
              checked={stayLoggedIn}
              sx={{ padding: 0 }}
              size="small"
              style={{
                color: !stayLoggedIn ? "rgb(119, 119, 119)" : "black",
              }}
            />
            <Box
              sx={{ ...styles.body, textAlign: "left", fontSize: "0.83rem" }}>
              Remember me
            </Box>
          </Stack>
          <Button
            sx={{
              ...SurveyButtonStyle.defaultStyling,
              ...SurveyButtonStyle.primaryColorScale,
            }}
            disableRipple
            fullWidth
            onClick={handleLogin}>
            LOG IN
          </Button>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ width: "100%", paddingTop: "8px" }}>
            <Box sx={{ ...styles.bar, width: "100%" }} />
          </Stack>
          <Stack
            sx={{ ...styles.panel, paddingTop: 2 }}
            direction="column"
            alignItems="center"
            spacing="1rem">
            <Box sx={styles.noAccount}>Don't have an account?</Box>
            <Button
              sx={{
                ...SurveyButtonStyle.defaultStyling,
                ...SurveyButtonStyle.yellowColor,
              }}
              disableRipple
              fullWidth
              onClick={() => navigate(Slug.surveyInitial)}>
              SIGN UP FOR FETTI
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default Login;
